"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="235580" className="mx-auto max-w-screen-md px-6 py-16 lg:px-8 animate-pulse">
        <div className="mx-auto mb-6 h-12 w-3/4 rounded-lg bg-muted"></div>
        <div className="mb-10 flex justify-center gap-4">
          <div className="size-10 rounded-full bg-muted"></div>
          <div className="mt-2 h-6 w-32 rounded bg-muted"></div>
        </div>
        <div className="mb-12 h-[400px] w-full rounded-2xl bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-5/6 rounded bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="mx-auto max-w-screen-md px-6 py-32 text-center lg:px-8">
        <h1 className="mb-4 font-default text-3xl font-bold text-primary">Article introuvable</h1>
        <p className="mb-8 font-sans text-muted-foreground">L'actualité que vous recherchez n'est plus disponible ou a été déplacée.</p>
        <Link to="#">
          <Button className="bg-primary font-default font-bold text-primary-foreground hover:bg-primary/90 rounded-none px-8">
            Retour à l'accueil
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-screen-md px-6 py-16 lg:px-8">
      <header className="mb-10 text-center">
        <h1 className="mb-6 font-default text-4xl font-bold leading-tight text-foreground md:text-5xl uppercase tracking-tight">
          {post.title}
        </h1>
        <div className="flex items-center justify-center gap-4 text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-3">
              <Avatar className="size-12 border-2 border-primary shadow-md">
                <AvatarImage src={post.author.avatar?.["48"]} alt={post.author.name} />
                <AvatarFallback className="bg-secondary text-primary font-default font-bold">
                  {post.author.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start">
                <span className="font-sans font-bold text-foreground">{post.author.name}</span>
                {post.date && (
                  <time className="font-sans text-sm text-muted-foreground">
                    {new Date(post.date).toLocaleDateString("fr-FR", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </time>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-none border-b-4 border-primary shadow-2xl">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="max-h-[600px] w-full object-cover"
          />
        </div>
      )}

      <div
        className="prose prose-lg max-w-none font-sans text-foreground/90 prose-headings:font-default prose-headings:font-bold prose-headings:text-primary prose-headings:uppercase prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-img:rounded-none prose-img:border prose-img:border-border"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />
    </article>
  );
}

function RecommendedPosts() {
  const { posts, loading, error } = usePosts();

  if (loading || error || posts.length === 0) {
    return null;
  }

  return (
    <div className="border-t border-border bg-secondary/30 py-20">
      <div className="mx-auto max-w-screen-xl px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="font-default text-3xl font-bold text-primary md:text-4xl uppercase tracking-wider">
            Actualités & Tournois
          </h2>
          <p className="mt-4 font-sans text-lg text-muted-foreground">
            Restez informé des dernières compétitions et des performances de nos jeunes champions.
          </p>
        </div>
        
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {posts.map((post, i) => (
            <Card
              key={post.id}
              data-index={i}
              className="flex h-full flex-col overflow-hidden border-none bg-card shadow-lg transition-all hover:-translate-y-1 rounded-none"
            >
              {post.featuredImage && (
                <Link to={post.link || "#"} className="block aspect-[16/10] overflow-hidden">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-110"
                  />
                </Link>
              )}
              <CardHeader className="flex-none pb-4">
                <CardTitle className="line-clamp-2 font-default text-xl font-bold text-foreground uppercase">
                  <Link to={post.link || "#"} className="transition-colors hover:text-primary">
                    {post.title}
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <div
                  className="line-clamp-3 font-sans text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
                />
              </CardContent>
              <CardFooter className="pt-0">
                <Link to={post.link || "#"}>
                  <Button variant="link" className="px-0 font-default font-bold text-primary uppercase tracking-widest">
                    Lire la suite →
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };
      
  const mainQuery = new WP_Query(currentWPQueryParams);
  
  
  const recommendedQuery = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <section id="single-post-template-section" className="min-h-screen bg-background text-foreground">
      <PostsProvider wp_query={mainQuery}>
        <SinglePostContent />
      </PostsProvider>
      
      <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
        <RecommendedPosts />
      </PostsProvider>
    </section>
  );
}